import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from "react-i18next";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Chip, Divider, Skeleton, useTheme } from "@mui/material";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { formatValue, percentageFormat } from '../../../../../helpers/helpers';
import { UserContext } from '../../../../../context/UserContext';
import { NO_VALUE_MARK } from '../../../dashboardHelpers';

type NumericChartBodyAlignment = 'empty' | 'single' | 'double' | 'quad';

export interface NumericChartNumber {
    number?: number | undefined;
    currency?: string;
    description?: string;
    showAsPercentage?: boolean;
}

export interface NumericChartProps {
    title: string;
    subtitle: string;
    mainNumber: number | undefined;
    mainCurrency?: string;
    numbers?: NumericChartNumber[];
    buttonText?: string;
    alignment: NumericChartBodyAlignment;
    isLoading?: boolean;
    isError?: boolean;
    linkChart?: string;
    forceNoData?: boolean;
}

const NumericChart = ({
    title,
    subtitle,
    mainNumber,
    mainCurrency,
    numbers,
    buttonText,
    alignment,
    isLoading,
    isError,
    linkChart,
    forceNoData,
}: NumericChartProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { locale } = useContext(UserContext);

    const mainNumberFormatted = useMemo(() => {
        if (forceNoData || !mainNumber) return NO_VALUE_MARK
        return mainCurrency ? formatValue(mainNumber, Boolean(mainCurrency), mainCurrency, locale, 2, false, true) : mainNumber
    }, [mainNumber, mainCurrency, locale, forceNoData]);

    const numbersFormatted = useMemo(() => {
        if (!numbers || alignment === 'empty') return []
        return numbers.map((n) => {
            if (forceNoData || !n?.number) return NO_VALUE_MARK
            return n.showAsPercentage
                ? percentageFormat(n.number, locale, 2)
                : formatValue(n.number, Boolean(n.currency), n.currency, locale, 2, false, true)
        })
    }, [numbers, alignment, locale, forceNoData])

    const rows = useMemo(() => {
        if (alignment === 'single') return [[numbers?.[0]]]
        if (alignment === 'double') return [[numbers?.[0], numbers?.[1]]]
        if (alignment === 'quad') return [
            [numbers?.[0], numbers?.[1]],
            [numbers?.[2], numbers?.[3]]
        ]
        return []
    }, [numbers, alignment])

    return (
        <>
            {!isLoading && (
                <Card elevation={0} sx={{
                    border: `solid 1px`,
                    borderColor: theme.palette.grey['12p'],
                    marginX: '8px',
                    padding: '12px',
                    flex: 1,
                    breakInside: 'avoid',
                    pageBreakInside: 'avoid',
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                    <Box sx={{
                        height: '100%',
                        flex: 1,
                    }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: 'auto', alignItems: 'center', textAlign: 'center', margin: '6px' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', marginBottom: '8px' }}>
                                <Typography variant="eventTitle">{title}</Typography>
                                {mainNumberFormatted === NO_VALUE_MARK && <Chip icon={<WarningAmberIcon />} label={t("screens.dashboarding.dataUnavailable")}  size='small'
                                sx={{
                                    backgroundColor: `${theme?.palette.warning['190p']}`,
                                    marginBottom: '4px',
                                    '& .MuiChip-label': {
                                        color: theme.palette.warning['160p']
                                    }
                                }} />}
                                {mainNumberFormatted !== NO_VALUE_MARK &&
                                    <Typography variant="body2" sx={{ marginTop: '5px', marginBottom: '8px' }}>
                                        {subtitle}
                                    </Typography>
                                }
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto' }}>
                                <Typography sx={{ fontSize: '34px', fontWeight: '600' }}>
                                    {mainNumberFormatted}
                                </Typography>
                            </Box>
                        </Box>

                        {rows.map((row, i) => (
                            <Box
                                key={title + row.map(r => r?.description).join('-')}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    margin: '6px',
                                    marginBottom: i === rows.length - 1 ? '8px' : '6px'
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'stretch',
                                        width: '100%',
                                        maxWidth: 320
                                    }}
                                >
                                    {row.map((item, j) => (
                                        item && (
                                            <React.Fragment key={subtitle.concat(item?.description?.replace(' ', '-') || '')}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flex: 1,
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        textAlign: 'center'
                                                    }}
                                                >
                                                    <Typography sx={{ fontSize: '20px', fontWeight: '600', }}>
                                                        {numbersFormatted[(i * 2) + j]}
                                                    </Typography>
                                                    <Typography variant="subtitle" sx= {{ fontFamily: 'Open Sans' }}>
                                                        {item.description}
                                                    </Typography>
                                                </Box>

                                                {row?.length > 1 && j % 2 === 0 && (
                                                    <Divider orientation="vertical" flexItem />
                                                )}
                                            </React.Fragment>
                                        )
                                    ))}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                    {buttonText &&
                        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 'auto' }}>
                            <Button href={linkChart} sx={{ fontSize: '16px', flex: 1, fontWeight: '600', color: 'primary.main' }}>
                                {buttonText}
                            </Button>
                        </Box>
                    }
                </Card>
            )}
            {isLoading &&
                <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', marginTop: '16px' }}>
                    <Card elevation={0} sx={{ border: 'solid 1px', borderColor: theme.palette.grey['12p'], marginX: '8px', padding: '12px', flex: 1 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto', justifyContent: 'space-between', margin: '6px' }}>
                            <Box>
                                <Skeleton variant="text" width="80%" height={18} sx={{ marginBottom: '10px' }} />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto' }}>
                                <Skeleton variant="text" width={50} height={34} sx={{ marginRight: '8px' }} />
                                <Skeleton variant="text" width={60} height={34} />
                            </Box>
                        </Box>
                    </Card>
                </Box>
            }
        </>
    )
}

export default NumericChart;
